import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ApiResponse } from '@models/api-response';
import { School } from '@models/school';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BrandedFallbackDownloadComponent
} from '@shared/branded-fallback-download/branded-fallback-download.component';
import { Helpers } from '@shared/helpers';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import {  switchMap, tap } from 'rxjs/operators';
import { SchoolsService } from '../../../../schools/schools.service';

@Component({
  selector: 'zbp-create-student-passwords-modal',
  templateUrl: './create-student-passwords-modal.component.html',
  styleUrls: ['./create-student-passwords-modal.component.scss'],
  standalone: false
})
export class CreateStudentPasswordsModalComponent implements OnInit, OnDestroy {
  constructor(
    public activeModal: NgbActiveModal,
    private schoolsService: SchoolsService,
    private toastr: ToastrService,
  ) {}

  protected readonly cancelToNotGenerateText = 'Click Cancel to not generate new passwords';
  protected readonly confirmToGenerateText = 'Click Confirm to generate new passwords';
  downloadFile: Blob;
  filename: string = null;
  schoolId: string = null;
  hasSuccessfullyResetInApi: boolean = false;
  isRegeneratingPasswords: boolean = false;
  private subscriptions: Subscription[] = [];

  cancelAriaLabel$: BehaviorSubject<string> = new BehaviorSubject<string>(this.cancelToNotGenerateText);
  confirmAriaLabel$: BehaviorSubject<string> = new BehaviorSubject<string>(this.confirmToGenerateText);

  @Input() school: School;

  @ViewChild('exportDownload') exportDownload: BrandedFallbackDownloadComponent;
  @ViewChild('redownloadLink', { static: true }) redownloadLink: ElementRef<HTMLAnchorElement>;

  get cancelAriaLabel(): string {
    return this.cancelAriaLabel$.value;
  }

  get confirmAriaLabel(): string {
    return this.confirmAriaLabel$.value;
  }

  ngOnInit(): void {
    this.filename = `${this.school.schoolId}_student_passwords`;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  generateStudentPasswords() {
    // Do not reset multiple times - reduces multiple API calls & helps user not save CSV with old passwords
    if (!this.hasSuccessfullyResetInApi) {
      this.isRegeneratingPasswords = true;

      this.subscriptions.push(
        this.schoolsService.createNewStudentPasswords(this.school.schoolId)
          .pipe(
            tap((res: ApiResponse<Blob>) => {
              if (res.success) {
                this.downloadFile = res.response;
                this.hasSuccessfullyResetInApi = true;
                this.setupAriaAfterGeneratingNew();
                this.isRegeneratingPasswords = false;
              } else {
                this.hasSuccessfullyResetInApi = false;
                this.toastr.error(res.messages[0]);
                this.isRegeneratingPasswords = false;
              }
            }),
            switchMap((res: ApiResponse<Blob>) => {
              if (res.success) {
                return this.downloadPasswordCSV();
              }
              return of(null);
            }),
          )
          .subscribe(),
      );
    } else {
      this.redownloadExistingCSV();
    }
  }

  downloadPasswordCSV(): Observable<boolean> {
    this.schoolId = this.school.abbreviatedSchoolId ? `${this.school.abbreviatedSchoolId}` : this.school.name.replace(' ', '_');
    this.filename =  `${this.schoolId}_student_passwords`;
    return Helpers.downloadFile(this.exportDownload, `${this.filename}`, this.downloadFile).pipe(
      tap((res) => {
        if (!res) {
          // In case anything errored within Download Fallback Comoponent, regenerate to kick off process again
          this.generateStudentPasswords();
        }
      })
    );
  }

  redownloadExistingCSV() {
    if (this.exportDownload.link?.nativeElement) {
      this.exportDownload.link?.nativeElement?.click();
    } else {
      // In case anything errored within Download Fallback Comoponent, trigger a new download with existing CSV
      this.downloadPasswordCSV();
    }
  }

  private setupAriaAfterGeneratingNew() {
    this.cancelAriaLabel$.next('Click Cancel to close window');
    this.confirmAriaLabel$.next('Click Confirm to re-download spreadsheet');
  }
}
